/**
 * MPS-263 KYC-104 — the declaration of truth.
 *
 * Author: Akarsh Singh | 2026-08-31
 *
 * The wording lives here rather than in the database for the same reason the
 * country rules do: it is a statement the business is making, it changes
 * rarely, and when it changes somebody should review the change before it
 * reaches a supplier.
 *
 * WHEN YOU CHANGE THE WORDING, BUMP THE VERSION.
 * The version accepted is stored against the application. A supplier whose
 * stored version is older than this one is asked to accept again at their next
 * submission, which is the whole point of versioning it — an old acceptance
 * cannot stand in for wording it never showed.
 */

export const KYC_CONSENT_TEXT_VERSION = '1.0';

export interface KycConsentDeclaration {
  version: string;
  /** Shown beside the checkbox. Written to be read, not skimmed past. */
  text: string;
  /** The smaller line underneath, for what follows a false declaration. */
  footnote: string;
}

export const KYC_CONSENT_DECLARATION: KycConsentDeclaration = {
  version: KYC_CONSENT_TEXT_VERSION,
  text:
    'I confirm that the information and documents in this application are ' +
    'true, complete and current, that I am authorised to submit them on ' +
    'behalf of this company, and that the owners named in the declaration ' +
    'are the real beneficial owners.',
  footnote:
    'A false declaration can result in the application being rejected and the ' +
    'account being suspended.',
};

/**
 * True when a previously recorded acceptance is still good.
 *
 * An application that has never been submitted has no stored version, and a
 * version that no longer matches the current wording is treated as no
 * acceptance at all rather than as a stale one.
 */
export function isConsentStillValid(storedVersion?: string | null): boolean {
  return (storedVersion || '').trim() === KYC_CONSENT_TEXT_VERSION;
}
